import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { ConfiguredMiddleware } from "wretch";
import { msalApp } from "../context/authContext";

export type AuthOptions = {
  scopes: string[];
};

type AuthMiddleware = (options?: AuthOptions) => ConfiguredMiddleware;

export const authMiddleware: AuthMiddleware =
  ({ scopes } = { scopes: [] }) =>
  (next) =>
  (url, opts) => {
    let account = msalApp.getActiveAccount();

    // Handle case where no active account is set
    if (!account) {
      const allAccounts = msalApp.getAllAccounts();

      if (allAccounts.length === 0) {
        const msg = "No authenticated user account was found.";
        console.error(msg);
        throw Error(msg);
      } else if (allAccounts.length === 1) {
        // If there's only one account, set it as the active account
        account = allAccounts[0] ?? null;
        msalApp.setActiveAccount(account);
      } else {
        // If multiple accounts exist, prompt the user to select one
        console.warn("Multiple accounts detected. Prompting user to select one.");
        return msalApp
          .loginPopup({ prompt: "select_account", scopes })
          .then((response) => {
            if (response.account) {
              msalApp.setActiveAccount(response.account);
              return authMiddleware({ scopes })(next)(url, opts); // Retry with the selected account
            } else {
              throw Error("Account selection failed.");
            }
          })
          .catch((error) => {
            console.error("Error during account selection:", error);
            throw error;
          });
      }
    }

    // Proceed with the active account
    return msalApp
      .acquireTokenSilent({
        scopes,
        account: account ?? undefined,
      })
      .then((res) => {
        return next(url, {
          ...opts,
          headers: {
            ...opts["headers"],
            Authorization: `Bearer ${res.accessToken}`,
          },
        });
      })
      .catch((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          msalApp.loginRedirect();
        }
        return Promise.reject(error);
      });
  };
